/**
 * THE STATUS VOCABULARY. One map, every surface.
 *
 * UI-UX-SPEC §2 is emphatic about this and it is right to be: the mockups render the same state
 * three ways — `Registration open` / `Registration` / `Registering`, `Live today` / `Today` /
 * `Live` — and a director who sees three names for one thing reasonably concludes they are three
 * things. The spec's list is the tiebreak; this file is that list, and nothing anywhere writes a
 * status label by hand.
 *
 * It lives in `shared` rather than in the component library because a recap email and a print pack
 * say "Registration open" too, and neither can import React. Same reason the brand tokens are here.
 *
 * Deliberately NOT derived from the database enums. A stored value is a storage concern with its
 * own migration cost; what a person reads is a product decision, and they are allowed to differ —
 * `registration` in the column, "Registration open" on the screen. A test in `apps/api` asserts
 * that every stored value has a label here, so they may differ but cannot drift apart.
 */

/** How a chip is coloured. Each tint has exactly one text token that may sit on it. */
export type StatusTone = 'neutral' | 'positive' | 'accent' | 'negative';

export interface StatusMeaning {
  label: string;
  tone: StatusTone;
}

/**
 * A league's life. Keys 1:1 with the `league_status` enum, plus one derived state.
 *
 * `live_tosession` is NOT stored and never will be — it is "active, and one of its sessions is
 * today", which is a question about the clock rather than a fact about the row. Storing it would
 * mean a job that flips it at midnight and gets it wrong across time zones.
 *
 * It is the only value that earns the accent: it means "something is happening right now".
 */
export const LEAGUE_STATUS = {
  draft: { label: 'Draft', tone: 'neutral' },
  registration: { label: 'Registration open', tone: 'accent' },
  active: { label: 'Active', tone: 'positive' },
  completed: { label: 'Completed', tone: 'neutral' },
  archived: { label: 'Archived', tone: 'neutral' },

  /** Derived, never stored. */
  live_tosession: { label: 'Live today', tone: 'accent' },
} as const satisfies Record<string, StatusMeaning>;

/**
 * A place on a roster — and the one vocabulary that does not map to a single column.
 *
 * `enrollment_status` stores `enrolled | waitlist | dropped | registered`, but what a director
 * needs to see on an enrolled player is whether they have PAID, which lives in
 * `enrollments.fee_paid`. So the chip is derived from two columns; `rosterStatus()` below is the
 * only thing that should do it.
 */
export const ROSTER_STATUS = {
  paid: { label: 'Paid', tone: 'positive' },
  unpaid: { label: 'Unpaid', tone: 'accent' },
  /**
   * Signed up on the public site and not yet confirmed. Distinct from `unpaid`, which is somebody
   * the DIRECTOR added and is chasing — this one she has never spoken to, and the action is to
   * check the booking page and confirm them. Same tone, because both are hers to act on, but not
   * the same word: telling them apart is the difference between "chase Ruth" and "who is Ruth".
   */
  registered: { label: 'Registered', tone: 'accent' },
  waitlist: { label: 'Waitlist', tone: 'neutral' },
  dropped: { label: 'Dropped', tone: 'negative' },
} as const satisfies Record<string, StatusMeaning>;

/**
 * Where a print-and-ship kit has got to. Keys 1:1 with the `kit_status` enum, plus two the spec
 * asks for that the column does not hold.
 */
export const KIT_STATUS = {
  pending: { label: 'Pending', tone: 'neutral' },
  files_ready: { label: 'Files ready', tone: 'neutral' },
  printing: { label: 'Printing', tone: 'accent' },
  assembled: { label: 'Assembled', tone: 'accent' },
  shipped: { label: 'Shipped', tone: 'positive' },
  delivered: { label: 'Delivered', tone: 'positive' },
  /** Durable director kits only — a consumable kit is never expected back. */
  returned: { label: 'Returned', tone: 'neutral' },

  /**
   * Derived. `not_shipped` is "the league starts in N days and `shipped_at` is still null";
   * `stale` is "the pack was generated, then the roster changed underneath it".
   *
   * Both are negative rather than a softer warning tone, and `stale` especially: printing a stale
   * pack wastes a real kit, a real shipping label, and a director's evening.
   */
  not_shipped: { label: 'Not shipped', tone: 'negative' },
  stale: { label: 'Stale', tone: 'negative' },
} as const satisfies Record<string, StatusMeaning>;

/**
 * One of a league's three sessions. Not in the spec's §2 list — it catalogues the four vocabularies
 * the mockups draw — but a session status renders on the league page, and the alternative was the
 * one place a raw enum value reached a director's screen.
 *
 * `voided` is negative rather than neutral because a session that did not happen is not a neutral
 * event: a room was booked, people cleared an afternoon, and the standings now have a hole in them.
 *
 * THE TONES SAY WHOSE MOVE IT IS. `pending_certification` is a session waiting on the DIRECTOR and
 * takes the accent rather than reading as progress — she is the only person who can move it, and a screen
 * that shows it in the same tone as `certified` hides the one item on her list that is hers.
 * `reopened` is likewise not neutral: a session that already counted is unlocked, and standings are
 * showing numbers somebody is currently editing.
 */
export const SESSION_STATUS = {
  open: { label: 'Not started', tone: 'neutral' },
  player_submission: { label: 'Scores coming in', tone: 'neutral' },
  pending_certification: { label: 'Needs your sign-off', tone: 'accent' },
  certified: { label: 'Signed off', tone: 'positive' },
  /**
   * ⚠ "PUBLISHED" WAS THE PLATFORM'S WORD, NOT HERS — renamed 2026-09-19, owner.
   *
   * It described what the system did to the row. A director reading her own league wants to know
   * the night is finished and her players can see where they stand — and it sat directly above
   * "Scores in for 15 of 18", which reads as a contradiction: published, with three missing.
   *
   * ⚠ AND NOTHING IN THE PRODUCT EVER SETS THIS. Every writer of `sessions.status` past `open` is
   * the demo seed or the load-check script; no screen, button or route signs a session off or
   * publishes one. So five of the seven values here are unreachable, and a real league shows "Not
   * started" on every row forever — including nights where every score is in.
   *
   * Renaming is what was asked for and is worth doing. It is not the fix. See DECISIONS.md.
   */
  published: { label: 'Results are out', tone: 'positive' },
  reopened: { label: 'Reopened for a correction', tone: 'accent' },
  voided: { label: 'Did not happen', tone: 'negative' },
} as const satisfies Record<string, StatusMeaning>;

/**
 * AN INCIDENT — something went wrong that a director could not settle on her own.
 *
 * `open` is negative rather than neutral on purpose. Every other vocabulary here uses neutral for
 * "nothing has happened yet", and for an incident that is exactly wrong: an unread incident IS the
 * problem, and a grey chip beside it says the platform has not noticed.
 *
 * `investigating` takes the accent. Somebody has picked it up and it is not finished — the same
 * reading the accent carries everywhere else on the platform.
 */
export const INCIDENT_STATUS = {
  open: { label: 'Open', tone: 'negative' },
  investigating: { label: 'Looking into it', tone: 'accent' },
  resolved: { label: 'Resolved', tone: 'positive' },
} as const satisfies Record<string, StatusMeaning>;

/**
 * What KIND of thing went wrong, in the words a director would use on the phone.
 *
 * Five, and no more, because a category list that needs scrolling is a category list where
 * everything ends up under "other". The order is roughly how often HQ expects to see them.
 */
export const INCIDENT_CATEGORY = {
  dispute: {
    label: 'A disagreement about scoring',
    hint: 'A hand, a total, or who won the session.',
  },
  venue: {
    label: 'The room',
    hint: 'Locked out, double-booked, too cold, no parking.',
  },
  conduct: {
    label: 'How somebody behaved',
    hint: 'Anything that made the session unpleasant for other players.',
  },
  equipment: {
    label: 'Tiles or the pack',
    hint: 'Missing tiles, a damaged set, score sheets that did not arrive.',
  },
  other: {
    label: 'Something else',
    hint: 'Anything that does not fit above.',
  },
} as const;

export type IncidentStatusKey = keyof typeof INCIDENT_STATUS;
export type IncidentCategoryKey = keyof typeof INCIDENT_CATEGORY;

export const ATTENTION_STATUS = {
  needs_attention: { label: 'Needs attention', tone: 'negative' },
} as const satisfies Record<string, StatusMeaning>;

export type LeagueStatusKey = keyof typeof LEAGUE_STATUS;
export type SessionStatusKey = keyof typeof SESSION_STATUS;
export type RosterStatusKey = keyof typeof ROSTER_STATUS;
export type KitStatusKey = keyof typeof KIT_STATUS;

/**
 * The roster chip for one enrollment, from the two columns that decide it.
 *
 * Status wins over payment: a dropped player who paid reads `Dropped`, because what the director
 * needs to know is that they are gone. The refund is a separate conversation.
 */
export function rosterStatus(enrollment: {
  status: 'enrolled' | 'waitlist' | 'dropped' | 'registered';
  feePaid?: boolean;
  waitlistPosition?: number | null;
}): StatusMeaning {
  if (enrollment.status === 'dropped') return ROSTER_STATUS.dropped;

  /**
   * Ahead of the payment check, because `fee_paid` on a self-registration means nothing yet —
   * nobody has looked. Reading it would render "Unpaid" on somebody the director has never heard
   * of, which reads as a debt rather than as an introduction.
   */
  if (enrollment.status === 'registered') return ROSTER_STATUS.registered;

  if (enrollment.status === 'waitlist') {
    /**
     * `Waitlist · 3`. The position is the thing a person asks about by name — "am I third or
     * fourth?" — so it belongs on the chip rather than in a tooltip. Position 0 would be a bug, so
     * an absent one renders the bare label rather than inventing a number.
     */
    return enrollment.waitlistPosition == null
      ? ROSTER_STATUS.waitlist
      : { label: `Waitlist · ${enrollment.waitlistPosition}`, tone: 'neutral' };
  }

  return enrollment.feePaid ? ROSTER_STATUS.paid : ROSTER_STATUS.unpaid;
}

/**
 * `3 seats left`. Under eight it is the thing to act on and takes the accent; above that it is
 * information. Zero is not a chip — a full league says "Join the waitlist" instead, which is a
 * different control rather than a differently-coloured label.
 */
export function seatsLeft(seats: number): StatusMeaning {
  return {
    label: seats === 1 ? '1 seat left' : `${seats} seats left`,
    tone: seats <= 7 ? 'accent' : 'positive',
  };
}

/**
 * A SESSION'S SEATING — proposed, or published to the players.
 *
 * `published` is POSITIVE, not accent. It shipped as a hand-built pill filled with the accent at
 * 25% alpha, which said "this needs you" about the one thing on the screen that is finished:
 * publishing is the end of the seating job, not a step in it. Sage is the token for done.
 *
 * It exists as a vocabulary rather than a string in the component because that is the rule — the
 * chip has no prop to pass text through, precisely so a state cannot be spelled three ways.
 */
export const SEATING_STATUS = {
  draft: { label: 'Not published', tone: 'neutral' },
  published: { label: 'Published', tone: 'positive' },
} as const satisfies Record<string, StatusMeaning>;

/**
 * A MESSAGE THE PLATFORM SENDS A PLAYER, and the eight states split in two.
 *
 * The first three are what WE did and are ours to control. The next two are what the world did and
 * arrive later by webhook. The last three are endings that are not delivery at all.
 *
 * `pending` takes the accent and nothing else does, because it is the only one that is somebody's
 * move: a message waiting for a director is the single item on this list she can act on, and a grey
 * chip beside it would hide it among seven states that need nothing.
 *
 * `bounced` and `failed` are both negative and are NOT the same thing, which is why they are worded
 * as far apart as they are. "Could not send" is ours — the provider never took it. "Did not arrive"
 * is theirs — we handed it over and the address refused it. One is a bug to chase and the other is
 * an address to correct, and a director can only tell which from the words.
 */
export const MESSAGE_STATUS = {
  pending: { label: 'Waiting for you', tone: 'accent' },
  scheduled: { label: 'Scheduled', tone: 'neutral' },
  sent: { label: 'Sent', tone: 'positive' },
  delivered: { label: 'Delivered', tone: 'positive' },
  bounced: { label: 'Did not arrive', tone: 'negative' },
  failed: { label: 'Could not send', tone: 'negative' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
  /** Still true when it was written, and something truer replaced it before it went. */
  superseded: { label: 'Replaced', tone: 'neutral' },
} as const satisfies Record<string, StatusMeaning>;

export type MessageStatusKey = keyof typeof MESSAGE_STATUS;

/**
 * WHETHER A DIRECTOR IS A FOUNDING DIRECTOR. Keys 1:1 with the `founding_status` enum.
 *
 * `founding` is NEUTRAL, and that is the considered choice rather than the modest one. Tone means
 * something on a chip, and there are twenty-five founding directors: colouring every one of their
 * rows would put twenty-five signal elements at rest on a single screen, where the budget is one.
 * The designation is a fact about a person, not a thing needing attention this morning.
 *
 * `none` has no words at all. "Standard" as a chip beside every non-founding director is a label
 * that says nothing, on the screen where it would be repeated most — the absence of the founding
 * chip already carries it.
 */
export const FOUNDING_STATUS = {
  founding: { label: 'Founding', tone: 'neutral' },
} as const satisfies Record<string, StatusMeaning>;

/**
 * WHAT A DIRECTOR'S MEMBERSHIP IS DOING. Keys 1:1 with the `subscription_status` enum.
 *
 * `past_due` is the one that has to be worded carefully. Stripe is still retrying the card and she
 * still has full access — a card expiring on Tuesday must not take the seating board away on
 * Wednesday — so the chip says the thing that is actually true and actionable, which is that the
 * payment did not go through. It takes the accent because it is the only state on this list
 * anybody can do something about.
 *
 * `canceled` is negative and `incomplete` is not, though both mean she cannot run a league:
 * incomplete is somebody who has not finished signing up yet, which is a funnel step rather than a
 * loss.
 */
export const SUBSCRIPTION_STATUS = {
  incomplete: { label: 'Not started', tone: 'neutral' },
  active: { label: 'Active', tone: 'positive' },
  past_due: { label: 'Payment failed', tone: 'accent' },
  canceled: { label: 'Cancelled', tone: 'negative' },
  comped: { label: 'Complimentary', tone: 'positive' },
} as const satisfies Record<string, StatusMeaning>;

/** Where the $49 call has got to. Keys 1:1 with the `launch_session_status` enum. */
export const LAUNCH_SESSION_STATUS = {
  /** Paid for and not yet in the diary. The only one that is the commissioner's move. */
  purchased: { label: 'To be booked', tone: 'accent' },
  scheduled: { label: 'Booked', tone: 'neutral' },
  completed: { label: 'Done', tone: 'positive' },
  no_show: { label: 'Missed', tone: 'negative' },
} as const satisfies Record<string, StatusMeaning>;

/** Every vocabulary, for the tests that prove nothing is written by hand. */
export const ALL_STATUS_VOCABULARIES = {
  league: LEAGUE_STATUS,
  session: SESSION_STATUS,
  seating: SEATING_STATUS,
  roster: ROSTER_STATUS,
  kit: KIT_STATUS,
  attention: ATTENTION_STATUS,
  incident: INCIDENT_STATUS,
  message: MESSAGE_STATUS,
  founding: FOUNDING_STATUS,
  subscription: SUBSCRIPTION_STATUS,
  launchSession: LAUNCH_SESSION_STATUS,
} as const;
